import Dexie, { type Table } from "dexie";
import type { AnalysisPreset } from "./analysis-presets";

interface CalibrationSample {
  id?: number;
  presetId: string;
  playerMoves: number;
  durationMs: number;
  createdAt: number;
}

class ChesslaDeviceDatabase extends Dexie {
  samples!: Table<CalibrationSample, number>;
  constructor() {
    super("chessla-device");
    this.version(1).stores({ samples: "++id,presetId,createdAt" });
  }
}

const database = new ChesslaDeviceDatabase();

export interface DeviceCalibration {
  sampleCount: number;
  typicalSeconds: number | null;
}

export const recordAnalysisCalibration = async (
  preset: AnalysisPreset,
  playerMoves: number,
  durationMs: number,
): Promise<void> => {
  if (!Number.isFinite(durationMs) || durationMs < 5_000 || playerMoves < 1) return;
  await database.samples.add({ presetId: preset.id, playerMoves, durationMs, createdAt: Date.now() });
  const stale = await database.samples.where("presetId").equals(preset.id).reverse().offset(24).primaryKeys();
  if (stale.length) await database.samples.bulkDelete(stale);
};

export const readDeviceCalibration = async (preset: AnalysisPreset): Promise<DeviceCalibration> => {
  const samples = await database.samples.where("presetId").equals(preset.id).reverse().limit(12).toArray();
  if (samples.length < 3) return { sampleCount: samples.length, typicalSeconds: null };
  const normalized = samples
    .map((sample) => sample.durationMs / 1_000 / sample.playerMoves)
    .sort((left, right) => left - right);
  const middle = normalized[Math.floor(normalized.length / 2)];
  return { sampleCount: samples.length, typicalSeconds: middle * 40 };
};
